import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useValueAssessmentTelemetry } from '@/hooks/useValueAssessmentTelemetry';

const COMMON_VALUES = [
  { name: 'Achievement', description: 'Accomplishing goals and being successful' },
  { name: 'Adventure', description: 'Seeking new experiences and challenges' },
  { name: 'Authenticity', description: 'Being true to yourself and genuine' },
  { name: 'Balance', description: 'Maintaining harmony between life areas' },
  { name: 'Collaboration', description: 'Working together with others' },
  { name: 'Compassion', description: 'Showing kindness and understanding' },
  { name: 'Creativity', description: 'Expressing ideas and innovation' },
  { name: 'Curiosity', description: 'Desire to learn and explore' },
  { name: 'Excellence', description: 'Striving for the highest quality' },
  { name: 'Family', description: 'Prioritizing loved ones and home' },
  { name: 'Freedom', description: 'Independence and autonomy' },
  { name: 'Growth', description: 'Continuous improvement and development' },
  { name: 'Health', description: 'Physical and mental wellbeing' },
  { name: 'Honesty', description: 'Truthfulness and integrity' },
  { name: 'Impact', description: 'Making a difference in the world' },
  { name: 'Innovation', description: 'Creating new solutions and ideas' },
  { name: 'Justice', description: 'Fairness and equity for all' },
  { name: 'Knowledge', description: 'Pursuit of wisdom and understanding' },
  { name: 'Leadership', description: 'Guiding and inspiring others' },
  { name: 'Loyalty', description: 'Faithfulness to commitments' },
  { name: 'Recognition', description: 'Being acknowledged for contributions' },
  { name: 'Relationships', description: 'Deep connections with others' },
  { name: 'Resilience', description: 'Bouncing back from adversity' },
  { name: 'Respect', description: 'Valuing others and being valued' },
  { name: 'Security', description: 'Stability and safety' },
  { name: 'Service', description: 'Helping and contributing to others' },
  { name: 'Spirituality', description: 'Connection to higher meaning' },
  { name: 'Wealth', description: 'Financial prosperity and abundance' },
  { name: 'Wisdom', description: 'Deep understanding and insight' },
  { name: 'Work-Life Integration', description: 'Blending career and personal life' },
];

interface ValuesCardSortProps {
  onComplete: (values: { name: string; description: string; importance: number }[]) => void;
  onCancel: () => void;
  initialDraftData?: Record<string, unknown>;
  onSaveDraft?: (data: Record<string, unknown>) => void;
  assessmentId?: string | null;
}

type Category = 'unassigned' | 'very' | 'somewhat' | 'not';

export const ValuesCardSort = ({ onComplete, onCancel, initialDraftData, onSaveDraft, assessmentId }: ValuesCardSortProps) => {
  const [step, setStep] = useState<'sort' | 'narrow'>(() => {
    if (initialDraftData?.step === 'narrow') return 'narrow';
    return 'sort';
  });
  const [categories, setCategories] = useState<Record<string, Category>>(() => {
    if (initialDraftData?.categories) {
      return initialDraftData.categories as Record<string, Category>;
    }
    const initial: Record<string, Category> = {};
    COMMON_VALUES.forEach(v => { initial[v.name] = 'unassigned'; });
    return initial;
  });
  const [topFive, setTopFive] = useState<string[]>(() => {
    if (initialDraftData?.topFive) return initialDraftData.topFive as string[];
    return [];
  });

  const {
    startAssessment,
    restoreSession,
    updateProgress,
    completeAssessment,
    abandonAssessment,
    trackValueSelection
  } = useValueAssessmentTelemetry();

  // Start or restore tracking when component mounts
  useEffect(() => {
    if (initialDraftData && assessmentId) {
      restoreSession(assessmentId, 'values_card_sort');
    } else {
      startAssessment('values_card_sort', 'dashboard');
    }
  }, []);

  const moveToCategory = (valueName: string, category: Category) => {
    const newCategories = { ...categories, [valueName]: category };
    setCategories(newCategories);

    // Track progress - count how many cards have been sorted
    const sortedCount = Object.values(categories).filter(c => c !== 'unassigned').length + 1;
    updateProgress(sortedCount, {
      card_sort_categories: {
        very: Object.values(newCategories).filter(c => c === 'very').length,
        somewhat: Object.values(newCategories).filter(c => c === 'somewhat').length,
        not: Object.values(newCategories).filter(c => c === 'not').length,
      }
    });

    // Auto-save draft
    onSaveDraft?.({ schema_version: 1, categories: newCategories, step, topFive });
  };

  const getValuesByCategory = (category: Category) => 
    COMMON_VALUES.filter(v => categories[v.name] === category);

  const unassignedCount = getValuesByCategory('unassigned').length;
  const veryImportant = getValuesByCategory('very');

  const handleProceedToNarrow = () => {
    if (veryImportant.length < 5) {
      // If they have fewer than 5 "very important", just use what they have
      const finalValues = veryImportant.map((v, i) => ({
        name: v.name,
        description: v.description,
        importance: i + 1,
      }));
      completeAssessment(
        finalValues.map(v => v.name),
        { card_sort_categories: { very: veryImportant.length } }
      );
      onComplete(finalValues);
    } else {
      setStep('narrow');
      onSaveDraft?.({ schema_version: 1, categories, step: 'narrow', topFive });
    }
  };

  const toggleTopFive = (name: string) => {
    const isSelected = topFive.includes(name);
    trackValueSelection(name, !isSelected);

    setTopFive(prev => {
      const next = prev.includes(name)
        ? prev.filter(n => n !== name)
        : prev.length >= 5 ? prev : [...prev, name];
      onSaveDraft?.({ schema_version: 1, categories, step, topFive: next });
      return next;
    });
  };

  const handleComplete = () => {
    const finalValues = topFive.map((name, i) => {
      const value = COMMON_VALUES.find(v => v.name === name)!;
      return {
        name: value.name,
        description: value.description,
        importance: i + 1,
      };
    });
    
    completeAssessment(
      finalValues.map(v => v.name),
      { card_sort_categories: { very: veryImportant.length } }
    );
    onComplete(finalValues);
  };

  const handleCancel = () => {
    abandonAssessment('user_cancelled');
    onCancel();
  };

  if (step === 'narrow') {
    return (
      <div className="space-y-4">
        <div className="text-center mb-6">
          <h3 className="font-semibold text-lg">Narrow Down to Your Top 5</h3>
          <p className="text-sm text-muted-foreground">
            Select exactly 5 values that resonate most deeply ({topFive.length}/5)
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {veryImportant.map(value => (
            <button
              key={value.name}
              onClick={() => toggleTopFive(value.name)}
              className={`p-3 rounded-lg border text-left transition-all ${
                topFive.includes(value.name)
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-start justify-between">
                <span className="font-medium text-sm">{value.name}</span>
                {topFive.includes(value.name) && (
                  <Badge variant="default" className="text-xs ml-1">
                    #{topFive.indexOf(value.name) + 1}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{value.description}</p>
            </button>
          ))}
        </div>

        <div className="flex gap-2 pt-4">
          <Button variant="outline" onClick={() => setStep('sort')} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Button 
            onClick={handleComplete} 
            disabled={topFive.length !== 5}
            className="flex-1 gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            Save My Top 5 Values
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-lg">Sort Your Values</h3>
        <p className="text-sm text-muted-foreground">
          Categorize each value by how important it is to you
        </p>
        {unassignedCount > 0 && (
          <Badge variant="secondary" className="mt-2">
            {unassignedCount} remaining
          </Badge>
        )}
        <div className="flex items-center gap-3 mt-2">
          <Progress value={((COMMON_VALUES.length - unassignedCount) / COMMON_VALUES.length) * 100} className="flex-1 h-2" />
          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
            {COMMON_VALUES.length - unassignedCount} of {COMMON_VALUES.length}
          </span>
        </div>
      </div>

      {/* Sorting Interface */}
      <div className="grid grid-cols-3 gap-2 text-center text-xs font-medium mb-2">
        <span className="text-green-600">Very Important</span>
        <span className="text-amber-600">Somewhat</span>
        <span className="text-muted-foreground">Not Important</span>
      </div>

      {/* Current Card */}
      {unassignedCount > 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="p-4 text-center">
            {(() => {
              const current = getValuesByCategory('unassigned')[0];
              return (
                <>
                  <h4 className="font-semibold text-lg">{current.name}</h4>
                  <p className="text-sm text-muted-foreground mb-4">{current.description}</p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-green-500 text-green-600 hover:bg-green-50"
                      onClick={() => moveToCategory(current.name, 'very')}
                    >
                      Very Important
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-500 text-amber-600 hover:bg-amber-50"
                      onClick={() => moveToCategory(current.name, 'somewhat')}
                    >
                      Somewhat
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-muted text-muted-foreground hover:bg-muted"
                      onClick={() => moveToCategory(current.name, 'not')}
                    >
                      Not Important
                    </Button>
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-primary/50 bg-primary/5">
          <CardContent className="p-4 text-center">
            <Sparkles className="w-8 h-8 text-primary mx-auto mb-2" />
            <h4 className="font-semibold">All values sorted!</h4>
            <p className="text-sm text-muted-foreground mb-4">
              You marked {veryImportant.length} values as very important
            </p>
            <Button onClick={handleProceedToNarrow} className="gap-2">
              {veryImportant.length <= 5 ? 'Save Values' : 'Narrow to Top 5'}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Sorted Piles Preview */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="bg-green-50 dark:bg-green-950/30 rounded p-2 min-h-[60px]">
          <span className="text-green-700 dark:text-green-400">{getValuesByCategory('very').length}</span>
        </div>
        <div className="bg-amber-50 dark:bg-amber-950/30 rounded p-2 min-h-[60px]">
          <span className="text-amber-700 dark:text-amber-400">{getValuesByCategory('somewhat').length}</span>
        </div>
        <div className="bg-muted rounded p-2 min-h-[60px]">
          <span className="text-muted-foreground">{getValuesByCategory('not').length}</span>
        </div>
      </div>

      <Button variant="ghost" onClick={handleCancel} className="w-full">
        Cancel
      </Button>
    </div>
  );
};
